import { useEffect, useMemo, useState } from 'react';
import { Activity } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useEndpointClientTraffic, type ClientTrafficSeries } from '@/hooks/useEndpointClientTraffic';
import { RangeControl } from './RangeControl';
import { RANGE_MS, refreshMs, type TrafficRange } from './range';
import { summariseCapacity, type TrafficRow } from './rows';
import { TrafficMirror } from './TrafficMirror';

const NO_CAPACITY = summariseCapacity([]);
const CLIENT_DIRECTIONS = { in: 'from the client', out: 'to the client' };

function rowsOf(series: ClientTrafficSeries): TrafficRow[] {
  const byTs = new Map<number, TrafficRow>();
  const row = (ts: number): TrafficRow => {
    let r = byTs.get(ts);
    if (!r) {
      r = {
        ts,
        bitsIn: null,
        bitsOut: null,
        errorsIn: null,
        errorsOut: null,
        discardsIn: null,
        discardsOut: null,
        nonUnicastShare: null,
        inboundPps: null,
        capacity: null,
        capacityMirror: null,
        utilizationIn: null,
        utilizationOut: null,
      };
      byTs.set(ts, r);
    }
    return r;
  };
  for (const [ts, v] of series.bitsIn) row(ts).bitsIn = v;
  for (const [ts, v] of series.bitsOut) row(ts).bitsOut = v === null ? null : -v;
  return [...byTs.values()].sort((a, b) => a.ts - b.ts);
}

/**
 * The AP's side of a wireless client's link: the client's traffic as the AP counted it, one chart
 * per AP it was on in the window. The counterpart of a switch port's traffic for a wired endpoint.
 */
export function ClientTrafficSection({
  endpointId,
  range: controlledRange,
  onRangeChange,
}: {
  endpointId: string;
  range?: TrafficRange;
  onRangeChange?: (range: TrafficRange) => void;
}) {
  const [localRange, setLocalRange] = useState<TrafficRange>('1h');
  const range = controlledRange ?? localRange;
  const setRange = onRangeChange ?? setLocalRange;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => { setNow(Date.now()); }, refreshMs(range));
    return () => { clearInterval(id); };
  }, [range]);
  const bounds = useMemo(() => ({ start: now - RANGE_MS[range], end: now }), [now, range]);
  const { data, isLoading, isError } = useEndpointClientTraffic(endpointId, bounds);

  return (
    <section className="rounded-md border border-border-subtle p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <Activity className="h-4 w-4" />
          Client traffic
        </h4>
        <RangeControl value={range} onChange={setRange} />
      </div>
      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : isError ? (
        <p className="text-sm text-muted-foreground">The metric store did not answer.</p>
      ) : !data || data.series.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No traffic reported for this client in this window. Its access point reports per-client counters only
          through a controller or a Cisco AP&apos;s SNMP agent.
        </p>
      ) : (
        data.series.map((s) => (
          <TrafficMirror
            key={s.apDeviceId ?? 'unknown'}
            rows={rowsOf(s)}
            start={data.start}
            end={data.end}
            syncId={`client-${endpointId}`}
            capacity={NO_CAPACITY}
            caption={`As ${s.apDeviceName ?? 'its access point'} counted it`}
            mode="bits"
            directionLabels={CLIENT_DIRECTIONS}
          />
        ))
      )}
    </section>
  );
}
